import { defaultCountries, parseCountry } from 'react-international-phone';

/**
 * Reading a lead's number, and getting to it in one tap.
 *
 * The sheet held the same number as "971 58 541 6336", "971585416336" and
 * "+971 58 541 6336". Stored, it is now E.164 with nothing but digits, so it
 * joins and de-duplicates cleanly; shown, it is spaced the way the office
 * writes it. Those are two different jobs and this file does the second.
 */

// Every dial code the phone input knows, longest first — "1" must not win over
// "1242" when both start the number.
const DIAL_CODES = (() => {
    const set = new Set();
    defaultCountries.forEach((c) => set.add(parseCountry(c).dialCode));
    return [...set].sort((a, b) => b.length - a.length);
})();

export const digitsOf = (v) => String(v || '').replace(/\D/g, '');

function groupNational(n, dial) {
    // A UAE mobile reads 58 541 6336 here, and nowhere else does. It is 90% of
    // the list, so it is worth the special case; everything else groups in
    // threes, which is readable in any country.
    if (dial === '971' && n.length === 9) {
        return `${n.slice(0, 2)} ${n.slice(2, 5)} ${n.slice(5)}`;
    }
    const parts = n.match(/.{1,3}/g) || [n];
    // A trailing single digit looks like a typo; fold it into the group before.
    if (parts.length > 1 && parts[parts.length - 1].length === 1) {
        parts[parts.length - 2] += parts.pop();
    }
    return parts.join(' ');
}

/**
 * "+971585416336" → "+971 58 541 6336".
 *
 * Anything that cannot be read confidently comes back exactly as it was typed.
 * A few dozen backfilled rows carry a bare national number with no country
 * code, and guessing one turns 585814614 into a Venezuelan mobile — a made-up
 * fact, printed with the same confidence as a real one. Left alone instead.
 */
export function formatPhone(v) {
    const raw = String(v || '');
    const d = digitsOf(raw);
    if (!d) return '';
    if (d.startsWith('0') || d.length < 10) return raw;
    const dial = DIAL_CODES.find((code) => d.startsWith(code) && d.length > code.length + 5);
    if (!dial) return raw;
    return `+${dial} ${groupNational(d.slice(dial.length), dial)}`;
}

/** A chat with this lead, one tap away. Null when there is no usable number. */
export function waHref(v) {
    const d = digitsOf(v);
    return d.length >= 8 ? `https://wa.me/${d}` : null;
}
